import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, API } from "../App";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Separator } from "./ui/separator";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { 
  CalendarIcon, 
  Plus, 
  Trash2, 
  Save, 
  Users, 
  Truck, 
  Wrench, 
  UserCheck,
  CloudSun,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

export default function DailyEntry() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [trades, setTrades] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  
  // Check if user is view_only (read-only mode)
  const isViewOnly = user?.role === "view_only";

  const [formData, setFormData] = useState({
    site_id: "",
    date: format(new Date(), "yyyy-MM-dd"),
    weather_condition: "",
    temperature: "",
    day_labour: [],
    subcontractors: [],
    deliveries: [],
    equipment: [],
    visitors: [],
    main_achievements: ""
  });

  const weatherOptions = [
    { value: "sunny", label: "Sunny", icon: "☀️" },
    { value: "moderate", label: "Moderate", icon: "⛅" },
    { value: "overcast", label: "Overcast", icon: "☁️" },
    { value: "windy", label: "Windy", icon: "💨" },
    { value: "rain_lost_day", label: "Rain Lost Day", icon: "🌧️" },
    { value: "temperature_extreme", label: "Temperature Extreme", icon: "🌡️" }
  ];

  useEffect(() => {
    loadSites();
    loadTrades();
    loadCompanies();
  }, []);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      date: format(selectedDate, "yyyy-MM-dd")
    }));
  }, [selectedDate]);

  const loadSites = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/sites`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSites(data);
        if (data.length > 0) {
          setFormData(prev => ({ ...prev, site_id: data[0].id }));
        }
      }
    } catch (error) {
      console.error("Error loading sites:", error);
      toast.error("Failed to load sites");
    }
  };

  const loadTrades = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/trades?active_only=true`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTrades(data);
      }
    } catch (error) {
      console.error("Error loading trades:", error);
      toast.error("Failed to load trades");
    }
  };

  const loadCompanies = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/companies?active_only=true`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
    } catch (error) {
      console.error("Error loading companies:", error);
      toast.error("Failed to load companies");
    }
  };

  const addDayLabour = () => {
    setFormData(prev => ({
      ...prev,
      day_labour: [...prev.day_labour, {
        trade: "",
        company: "",
        number_of_persons: 1,
        start_time: "08:00",
        end_time: "17:00",
        total_hours: 8,
        number_of: 0,
        activity_summary: ""
      }]
    }));
  };

  const removeDayLabour = (index) => {
    setFormData(prev => ({
      ...prev,
      day_labour: prev.day_labour.filter((_, i) => i !== index)
    }));
  };

  const updateDayLabour = (index, field, value) => {
    setFormData(prev => {
      const newDayLabour = [...prev.day_labour];
      newDayLabour[index] = { ...newDayLabour[index], [field]: value };
      
      // Auto-calculate hours if start/end time changes
      if (field === 'start_time' || field === 'end_time') {
        const start = newDayLabour[index].start_time;
        const end = newDayLabour[index].end_time;
        if (start && end) {
          const startHours = parseInt(start.split(':')[0]) + parseInt(start.split(':')[1]) / 60;
          const endHours = parseInt(end.split(':')[0]) + parseInt(end.split(':')[1]) / 60;
          newDayLabour[index].total_hours = Math.max(0, endHours - startHours);
        }
      }
      
      return { ...prev, day_labour: newDayLabour };
    });
  };

  const addSubcontractor = () => {
    setFormData(prev => ({
      ...prev,
      subcontractors: [...prev.subcontractors, {
        company: "",
        number_of_persons: 1,
        activity_overview: ""
      }]
    }));
  };

  const removeSubcontractor = (index) => {
    setFormData(prev => ({
      ...prev,
      subcontractors: prev.subcontractors.filter((_, i) => i !== index)
    }));
  };

  const updateSubcontractor = (index, field, value) => {
    setFormData(prev => {
      const newSubcontractors = [...prev.subcontractors];
      newSubcontractors[index] = { ...newSubcontractors[index], [field]: value };
      return { ...prev, subcontractors: newSubcontractors };
    });
  };

  const addDelivery = () => {
    setFormData(prev => ({
      ...prev,
      deliveries: [...prev.deliveries, {
        description: "",
        time: "",
        supplier: ""
      }]
    }));
  };

  const removeDelivery = (index) => {
    setFormData(prev => ({
      ...prev,
      deliveries: prev.deliveries.filter((_, i) => i !== index)
    }));
  };

  const updateDelivery = (index, field, value) => {
    setFormData(prev => {
      const newDeliveries = [...prev.deliveries];
      newDeliveries[index] = { ...newDeliveries[index], [field]: value };
      return { ...prev, deliveries: newDeliveries };
    });
  };

  const addEquipment = () => {
    setFormData(prev => ({
      ...prev,
      equipment: [...prev.equipment, {
        name: "",
        type: "",
        operator: "",
        hours_operated: 0,
        status: "operational"
      }]
    }));
  };

  const removeEquipment = (index) => {
    setFormData(prev => ({
      ...prev,
      equipment: prev.equipment.filter((_, i) => i !== index)
    }));
  };

  const updateEquipment = (index, field, value) => {
    setFormData(prev => {
      const newEquipment = [...prev.equipment];
      newEquipment[index] = { ...newEquipment[index], [field]: value };
      return { ...prev, equipment: newEquipment };
    });
  };

  const addVisitor = () => {
    setFormData(prev => ({
      ...prev,
      visitors: [...prev.visitors, {
        name: "",
        company: "",
        purpose: "",
        time_in: "",
        time_out: ""
      }]
    }));
  };

  const removeVisitor = (index) => {
    setFormData(prev => ({
      ...prev,
      visitors: prev.visitors.filter((_, i) => i !== index)
    }));
  };

  const updateVisitor = (index, field, value) => {
    setFormData(prev => {
      const newVisitors = [...prev.visitors];
      newVisitors[index] = { ...newVisitors[index], [field]: value };
      return { ...prev, visitors: newVisitors };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.site_id || !formData.weather_condition) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/daily-records`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...formData,
          temperature: formData.temperature ? parseFloat(formData.temperature) : null
        })
      });

      if (response.ok) {
        toast.success("Daily record saved successfully!");
        navigate("/dashboard");
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to save daily record");
      }
    } catch (error) {
      console.error("Error saving record:", error);
      toast.error("Failed to save daily record");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 fade-in-up" data-testid="daily-entry-container">
      {/* Header */}
      <div className="mb-8">
        <h1 className="heading-primary text-4xl mb-2">Daily Entry</h1>
        <p className="text-lg text-slate-600">Record daily construction site activities</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <fieldset disabled={isViewOnly}>
        {/* Basic Information */}
        <Card className="dashboard-card" data-testid="basic-info-card">
          <CardHeader>
            <CardTitle className="heading-secondary flex items-center">
              <CalendarIcon className="w-5 h-5 mr-2" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="site-select" className="font-medium">Site *</Label>
                <Select value={formData.site_id} onValueChange={(value) => setFormData(prev => ({ ...prev, site_id: value }))} disabled={isViewOnly}>
                  <SelectTrigger data-testid="site-select">
                    <SelectValue placeholder="Select construction site" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Date *</Label>
                <Popover open={showCalendar} onOpenChange={setShowCalendar}>
                  <PopoverTrigger asChild disabled={isViewOnly}>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="date-picker-button"
                      disabled={isViewOnly}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(selectedDate, "d MMMM yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setShowCalendar(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="weather" className="font-medium">Weather Condition *</Label>
                <Select value={formData.weather_condition} onValueChange={(value) => setFormData(prev => ({ ...prev, weather_condition: value }))}>
                  <SelectTrigger data-testid="weather-select">
                    <SelectValue placeholder="Select weather condition" />
                  </SelectTrigger>
                  <SelectContent>
                    {weatherOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{option.icon}</span>
                          <span>{option.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="temperature" className="font-medium">Temperature (°C)</Label>
                <Input
                  id="temperature"
                  type="number"
                  placeholder="e.g., 25"
                  value={formData.temperature}
                  onChange={(e) => setFormData(prev => ({ ...prev, temperature: e.target.value }))}
                  data-testid="temperature-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="day-labour" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="day-labour" className="flex items-center space-x-1" data-testid="day-labour-tab">
              <Users className="w-4 h-4" />
              <span>Day Labour</span>
            </TabsTrigger>
            <TabsTrigger value="subcontractors" className="flex items-center space-x-1" data-testid="subcontractors-tab">
              <UserCheck className="w-4 h-4" />
              <span>Subcontractors</span>
            </TabsTrigger>
            <TabsTrigger value="deliveries" className="flex items-center space-x-1" data-testid="deliveries-tab">
              <Truck className="w-4 h-4" />
              <span>Deliveries</span>
            </TabsTrigger>
            <TabsTrigger value="equipment" className="flex items-center space-x-1" data-testid="equipment-tab">
              <Wrench className="w-4 h-4" />
              <span>Equipment</span>
            </TabsTrigger>
            <TabsTrigger value="visitors" className="flex items-center space-x-1" data-testid="visitors-tab">
              <UserCheck className="w-4 h-4" />
              <span>Visitors</span>
            </TabsTrigger>
          </TabsList>

          {/* Day Labour Tab */}
          <TabsContent value="day-labour">
            <Card className="dashboard-card" data-testid="day-labour-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="heading-secondary">Day Labour & Contractors</CardTitle>
                {!isViewOnly && (
                  <Button type="button" onClick={addDayLabour} className="btn-construction" data-testid="add-day-labour-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Day Labour
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {formData.day_labour.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No day labour added yet</p>
                    <p className="text-sm">Click "Add Day Labour" to get started</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {formData.day_labour.map((person, index) => (
                      <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-4" data-testid={`day-labour-item-${index}`}>
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium text-slate-900">Day Labour Group {index + 1}</h4>
                          {!isViewOnly && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeDayLabour(index)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`remove-day-labour-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label className="font-medium">Trade *</Label>
                            <Select 
                              value={person.trade} 
                              onValueChange={(value) => updateDayLabour(index, 'trade', value)}
                            >
                              <SelectTrigger data-testid={`day-labour-trade-${index}`}>
                                <SelectValue placeholder="Select trade" />
                              </SelectTrigger>
                              <SelectContent>
                                {trades.map((trade) => (
                                  <SelectItem key={trade.id} value={trade.name}>
                                    {trade.name}
                                    {trade.category !== 'general' && (
                                      <span className="text-xs text-slate-500 ml-2">({trade.category})</span>
                                    )}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="font-medium">Company *</Label>
                            <Select 
                              value={person.company} 
                              onValueChange={(value) => updateDayLabour(index, 'company', value)}
                            >
                              <SelectTrigger data-testid={`day-labour-company-${index}`}>
                                <SelectValue placeholder="Select company" />
                              </SelectTrigger>
                              <SelectContent>
                                {companies.filter(c => c.company_type === 'contractor' || c.company_type === 'subcontractor').map((company) => (
                                  <SelectItem key={company.id} value={company.name}>
                                    {company.name}
                                    <span className="text-xs text-slate-500 ml-2">({company.company_type})</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="font-medium">Number of Persons *</Label>
                            <Input
                              type="number"
                              min="1"
                              placeholder="Number of workers"
                              value={person.number_of_persons}
                              onChange={(e) => updateDayLabour(index, 'number_of_persons', parseInt(e.target.value) || 1)}
                              data-testid={`day-labour-persons-${index}`}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div className="space-y-2">
                            <Label className="font-medium">Start Time *</Label>
                            <Input
                              type="time"
                              value={person.start_time}
                              onChange={(e) => updateDayLabour(index, 'start_time', e.target.value)}
                              data-testid={`day-labour-start-time-${index}`}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-medium">End Time *</Label>
                            <Input
                              type="time"
                              value={person.end_time}
                              onChange={(e) => updateDayLabour(index, 'end_time', e.target.value)}
                              data-testid={`day-labour-end-time-${index}`}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-medium">Total Hours</Label>
                            <Input
                              type="number"
                              step="0.5"
                              value={person.total_hours}
                              onChange={(e) => updateDayLabour(index, 'total_hours', parseFloat(e.target.value))}
                              data-testid={`day-labour-hours-${index}`}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-medium">Days Off</Label>
                            <Input
                              type="number"
                              min="0"
                              value={person.number_of}
                              onChange={(e) => updateDayLabour(index, 'number_of', parseInt(e.target.value))}
                              data-testid={`day-labour-days-off-${index}`}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="font-medium">Activity Summary *</Label>
                          <Textarea
                            placeholder="Describe the tasks and activities performed..."
                            value={person.activity_summary}
                            onChange={(e) => updateDayLabour(index, 'activity_summary', e.target.value)}
                            rows={3}
                            data-testid={`day-labour-activity-${index}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subcontractors Tab */}
          <TabsContent value="subcontractors">
            <Card className="dashboard-card" data-testid="subcontractors-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="heading-secondary">Subcontractors</CardTitle>
                {!isViewOnly && (
                  <Button type="button" onClick={addSubcontractor} className="btn-construction" data-testid="add-subcontractor-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Subcontractor
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {formData.subcontractors.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <UserCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No subcontractors added yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.subcontractors.map((sub, index) => (
                      <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-4" data-testid={`subcontractor-item-${index}`}>
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium text-slate-900">Subcontractor {index + 1}</h4>
                          {!isViewOnly && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeSubcontractor(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="font-medium">Company Name *</Label>
                              <Select 
                                value={sub.company} 
                                onValueChange={(value) => updateSubcontractor(index, 'company', value)}
                              >
                                <SelectTrigger data-testid={`subcontractor-company-${index}`}>
                                  <SelectValue placeholder="Select subcontractor" />
                                </SelectTrigger>
                                <SelectContent>
                                  {companies.filter(c => c.company_type === 'subcontractor' || c.company_type === 'contractor').map((company) => (
                                    <SelectItem key={company.id} value={company.name}>
                                      {company.name}
                                      <span className="text-xs text-slate-500 ml-2">({company.company_type})</span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label className="font-medium">Number of Persons *</Label>
                              <Input
                                type="number"
                                min="1"
                                placeholder="Number of workers"
                                value={sub.number_of_persons}
                                onChange={(e) => updateSubcontractor(index, 'number_of_persons', parseInt(e.target.value) || 1)}
                                data-testid={`subcontractor-persons-${index}`}
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="font-medium">Activity Overview *</Label>
                            <Textarea
                              placeholder="Describe the subcontractor's activities and tasks..."
                              value={sub.activity_overview}
                              onChange={(e) => updateSubcontractor(index, 'activity_overview', e.target.value)}
                              rows={3}
                              data-testid={`subcontractor-activity-${index}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Deliveries Tab */}
          <TabsContent value="deliveries">
            <Card className="dashboard-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="heading-secondary">Deliveries</CardTitle>
                {!isViewOnly && (
                  <Button type="button" onClick={addDelivery} className="btn-construction" data-testid="add-delivery-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Delivery
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {formData.deliveries.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No deliveries recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.deliveries.map((delivery, index) => (
                      <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-4">
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium text-slate-900">Delivery {index + 1}</h4>
                          {!isViewOnly && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeDelivery(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label className="font-medium">Description *</Label>
                            <Input
                              placeholder="Materials delivered"
                              value={delivery.description}
                              onChange={(e) => updateDelivery(index, 'description', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-medium">Time</Label>
                            <Input
                              type="time"
                              value={delivery.time}
                              onChange={(e) => updateDelivery(index, 'time', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-medium">Supplier</Label>
                            <Input
                              placeholder="Supplier name"
                              value={delivery.supplier}
                              onChange={(e) => updateDelivery(index, 'supplier', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Equipment Tab */}
          <TabsContent value="equipment">
            <Card className="dashboard-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="heading-secondary">Equipment & Machinery</CardTitle>
                {!isViewOnly && (
                  <Button type="button" onClick={addEquipment} className="btn-construction">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Equipment
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {formData.equipment.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Wrench className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No equipment recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.equipment.map((equip, index) => (
                      <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-4">
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium text-slate-900">Equipment {index + 1}</h4>
                          {!isViewOnly && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeEquipment(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="font-medium">Equipment Name *</Label>
                            <Input
                              placeholder="e.g., Excavator, Crane"
                              value={equip.name}
                              onChange={(e) => updateEquipment(index, 'name', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-medium">Type</Label>
                            <Input
                              placeholder="Equipment type/model"
                              value={equip.type}
                              onChange={(e) => updateEquipment(index, 'type', e.target.value)}
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label className="font-medium">Operator</Label>
                            <Input
                              placeholder="Operator name"
                              value={equip.operator}
                              onChange={(e) => updateEquipment(index, 'operator', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-medium">Hours Operated</Label>
                            <Input
                              type="number"
                              step="0.5"
                              min="0"
                              value={equip.hours_operated}
                              onChange={(e) => updateEquipment(index, 'hours_operated', parseFloat(e.target.value))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-medium">Status</Label>
                            <Select value={equip.status} onValueChange={(value) => updateEquipment(index, 'status', value)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="operational">Operational</SelectItem>
                                <SelectItem value="maintenance">Maintenance</SelectItem>
                                <SelectItem value="repair">Under Repair</SelectItem>
                                <SelectItem value="idle">Idle</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Visitors Tab */}
          <TabsContent value="visitors">
            <Card className="dashboard-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="heading-secondary">Site Visitors</CardTitle>
                {!isViewOnly && (
                  <Button type="button" onClick={addVisitor} className="btn-construction">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Visitor
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {formData.visitors.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <UserCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No visitors recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.visitors.map((visitor, index) => (
                      <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-4">
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium text-slate-900">Visitor {index + 1}</h4>
                          {!isViewOnly && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeVisitor(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="font-medium">Name *</Label>
                            <Input
                              placeholder="Visitor name"
                              value={visitor.name}
                              onChange={(e) => updateVisitor(index, 'name', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-medium">Company *</Label>
                            <Input
                              placeholder="Company/Organization"
                              value={visitor.company}
                              onChange={(e) => updateVisitor(index, 'company', e.target.value)}
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label className="font-medium">Purpose *</Label>
                            <Input
                              placeholder="Visit purpose"
                              value={visitor.purpose}
                              onChange={(e) => updateVisitor(index, 'purpose', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-medium">Time In</Label>
                            <Input
                              type="time"
                              value={visitor.time_in}
                              onChange={(e) => updateVisitor(index, 'time_in', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-medium">Time Out</Label>
                            <Input
                              type="time"
                              value={visitor.time_out}
                              onChange={(e) => updateVisitor(index, 'time_out', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Achievements */}
        <Card className="dashboard-card" data-testid="achievements-card">
          <CardHeader>
            <CardTitle className="heading-secondary">Main Achievements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="achievements" className="font-medium">Daily Achievements & Progress</Label>
              <Textarea
                id="achievements"
                placeholder="Describe the main achievements, milestones, and progress made today..."
                value={formData.main_achievements}
                onChange={(e) => setFormData(prev => ({ ...prev, main_achievements: e.target.value }))}
                rows={4}
                data-testid="achievements-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/dashboard")}
            data-testid="cancel-button"
          >
            {isViewOnly ? "Back" : "Cancel"}
          </Button>
          {!isViewOnly && (
            <Button
              type="submit"
              className="btn-construction"
              disabled={isSubmitting}
              data-testid="save-daily-record-button"
            >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Saving...</span>
              </div>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Daily Record
              </>
            )}
          </Button>
          )}
        </div>
        </fieldset>
      </form>
    </div>
  );
}